import type { ProgrammaticLandingPageConfig } from '../types'
import { KWP_SIZE_FACTS } from './kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[15]

export const KWP_15_PHOTOVOLTAIK_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '15-kwp-photovoltaik-kosten',
  lang: 'de',
  topicId: 'kwp-kosten-15',
  title: '15 kWp Photovoltaikanlage: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet eine 15-kWp-Photovoltaikanlage? Dachfläche, Modulanzahl, Kostenaufschlüsselung und ' +
    'kostenloser Preisvergleich für große Häuser mit Wärmepumpe und E-Auto.',
  keywords: [
    '15 kWp Photovoltaik Kosten',
    '15 kWp Solaranlage Preis',
    'Photovoltaikanlage 15 kWp',
    'Was kostet eine 15 kWp Anlage',
    '15 kWp PV-Anlage großes Haus',
  ],
  eyebrow: 'Anlagengröße 15 kWp',
  h1: '15 kWp Photovoltaikanlage: Kosten und Richtwerte',
  intro:
    'Eine 15-kWp-Anlage passt zu großen Einfamilienhäusern oder Zweifamilienhäusern mit hohem ' +
    'Gesamtverbrauch, etwa durch Wärmepumpe und Elektroauto zusammen. Hier finden Sie Richtwerte ' +
    'zu Dachfläche und Modulanzahl, eine Kostenaufschlüsselung und direkt darunter einen Rechner ' +
    'mit dem für 15 kWp vorbelegten Durchschnittspreis aus echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp', kwp: 15 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich eine 15-kWp-Anlage?' },
    {
      type: 'paragraph',
      text:
        'Richtwert: 15 kWp passen typischerweise zu einem großen Einfamilienhaus oder ' +
        'Zweifamilienhaus mit Wärmepumpe und Elektroauto, Jahresverbrauch insgesamt oft im Bereich ' +
        'von 9.000-12.000 kWh. Bei zwei separaten Haushalten im selben Gebäude lohnt sich zusätzlich ' +
        'eine Beratung zur Aufteilung von Erzeugung und Verbrauch.',
    },
    { type: 'heading', text: 'Dachfläche und Module: das braucht eine 15-kWp-Anlage' },
    {
      type: 'paragraph',
      text:
        `Richtwert: rund ${FACTS.roofAreaM2} m² Dachfläche und ca. ${FACTS.moduleCount} Module bei ` +
        `marktüblichen ~440-Wp-Modulen. Der Jahresertrag liegt bei Südausrichtung ohne Verschattung ` +
        `bei etwa ${FACTS.annualYieldKwh.toLocaleString('de-DE')} kWh. Reicht die Hauptdachfläche ` +
        'nicht aus, prüfen Handwerksbetriebe oft eine Aufteilung auf mehrere Dachflächen mit ' +
        'unterschiedlicher Ausrichtung - eine genaue, dachspezifische Berechnung liefert der ' +
        'kostenlose PV-Rechner.',
    },
    { type: 'heading', text: 'Kostenbestandteile einer Photovoltaikanlage' },
    {
      type: 'list',
      items: [
        'Module: größter Einzelposten, meist rund ein Drittel der Gesamtkosten',
        'Wechselrichter: bei 15 kWp meist dreiphasig, teils mit mehreren Strings/Wechselrichtern',
        'Montage/Arbeitsleistung: Befestigung auf dem Dach, Verkabelung',
        'Gerüst: bei größerer Dachfläche entsprechend umfangreicher',
        'Elektroinstallation: Zählerschrank-Anpassung, Anschluss ans Hausnetz',
        'Anmeldung: Netzbetreiber und Marktstammdatenregister',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Ab etwa 15 kWp lohnt sich häufig ein genauerer Blick auf die Statik des Dachs und die ' +
        'Kapazität des Hausanschlusses - beides klärt der Handwerksbetrieb vor Ort im Rahmen des ' +
        'Angebots.',
    },
    { type: 'heading', text: 'Amortisation und Eigenverbrauch' },
    {
      type: 'paragraph',
      text:
        'Mit Wärmepumpe und Elektroauto als zusätzlichen Verbrauchern lässt sich bei einer ' +
        '15-kWp-Anlage ein hoher Eigenverbrauchsanteil erreichen, insbesondere wenn beide Geräte ' +
        'gezielt tagsüber laufen. Details zu Einspeisevergütung und Förderungen im Förder-Überblick, ' +
        'eine genaue Berechnung für Ihr Dach liefert der PV-Rechner.',
    },
  ],
  faq: [
    {
      q: 'Brauche ich für eine 15-kWp-Anlage eine Baugenehmigung?',
      a: 'In der Regel nicht - Photovoltaikanlagen auf bestehenden Dächern sind in den meisten Bundesländern genehmigungsfrei. Ausnahmen gelten z. B. bei Denkmalschutz; das prüft der Handwerksbetrieb im Zweifel vorab.',
    },
    {
      q: 'Für welche Gebäude eignet sich 15 kWp?',
      a: 'Typischerweise für große Einfamilienhäuser oder Zweifamilienhäuser mit hohem Gesamtverbrauch, etwa durch Wärmepumpe und Elektroauto zusammen.',
    },
    {
      q: 'Wie wirkt sich die Anlagengröße auf die Montagekosten aus?',
      a: 'Größere Anlagen wie 15 kWp haben pro kWp meist etwas günstigere Fixkosten (Anmeldung, Grundgerüst) als kleinere Anlagen, da sich diese Posten auf mehr installierte Leistung verteilen.',
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '12 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/12-kwp-photovoltaik-kosten' },
    { label: '20 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/20-kwp-photovoltaik-kosten' },
    { label: '25 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/25-kwp-photovoltaik-kosten' },
    { label: '15 kWh Batteriespeicher: Kosten & Richtwerte', href: '/15-kwh-speicher-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'Photovoltaik Schritt für Schritt planen', href: '/photovoltaik-planen' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
